/**
 * Panel Trigger Component
 *
 * The collapsed "Solomon" pill button that appears at the bottom of the screen.
 * Clicking expands the full Solomon panel.
 * Supports drag-to-move functionality for repositioning on screen.
 */

import { useRef, useCallback } from 'react';

import { useAudioPlayer } from '@pray/shared/components/ContentPlayer/FullscreenContentPlayer/FullscreenContentPlayer';
import { useAuth } from '@pray/shared/hooks/useAuth';
import { cn } from '@pray/shared/utils/styles';

import { Permissions } from 'utils/auth';

import { useDraggable } from '../hooks/useDraggable';
import type { PanelTriggerProps } from '../types';

import styles from './PanelTrigger.module.scss';

// Crown icon for Solomon AI branding
function CrownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 17l2-11 5 5 3-7 3 7 5-5 2 11H2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Threshold to distinguish click from drag (in pixels)
const DRAG_THRESHOLD = 5;

export function PanelTrigger({ isExpanded, onClick, isThinking = false, className }: PanelTriggerProps) {
  const { permissions } = useAuth();
  const isAdmin = permissions.has(Permissions.STUDIO_ADMIN);
  const { options: playerOptions } = useAudioPlayer();
  const isPlayerOpen = !!playerOptions?.contentId;
  const playerOffset = isPlayerOpen ? 92 : 0;

  // Draggable functionality — default above the bug report button (bottom-right)
  const { position, isDragging, dragProps } = useDraggable({
    defaultPosition: { x: 24, y: 72 },
  });

  // Track mouse/touch start position to distinguish clicks from drags
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);

  // Handle mouse down - track start position and forward to drag handler
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      dragProps.onMouseDown(e);
    },
    [dragProps]
  );

  // Handle touch start - track start position and forward to drag handler
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      dragProps.onTouchStart(e);
    },
    [dragProps]
  );

  // Handle click - only trigger if it wasn't a drag
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Check if this was actually a drag (moved more than threshold)
      if (dragStartRef.current) {
        const deltaX = Math.abs(e.clientX - dragStartRef.current.x);
        const deltaY = Math.abs(e.clientY - dragStartRef.current.y);
        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          // This was a drag, not a click
          e.preventDefault();
          return;
        }
      }
      onClick();
    },
    [onClick]
  );

  // Don't render for non-admin users
  if (!isAdmin) {
    return null;
  }

  // Don't render if panel is expanded
  if (isExpanded) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className={cn(
        styles.trigger,
        styles.draggable,
        isThinking && styles.isThinking,
        isDragging && styles.isDragging,
        className
      )}
      style={{
        ...dragProps.style,
        bottom: `${position.y + playerOffset}px`,
        transition: isDragging ? undefined : 'bottom 300ms ease',
      }}
      aria-label="Solomon"
      aria-expanded={isExpanded}
    >
      <CrownIcon className={cn(styles.icon, isThinking && styles.rotating)} />
    </button>
  );
}

export default PanelTrigger;
